import React from "react";
import { PopoverMenuItem, PopoverSeparator } from "../Popover/index.js";
import type { ActionMenuItem } from "./ActionMenu.js";

/* ---------------- ActionMenuItems ----------------
   The item list of an ActionMenu, moved out of ActionMenu.tsx unchanged so
   SplitButton's menu renders its items the same way: the same rows, the same
   `hint` and `disabledReason` lines and the same aria-describedby wiring.
   Internal: not in the barrel. It renders a fragment, so the menu's DOM is
   what it was when this block lived inside ActionMenu. */

export interface ActionMenuItemsProps {
  readonly items: readonly ActionMenuItem[];
  /** Namespaces the description ids to one mounted menu: the caller's
   *  `React.useId()`. */
  readonly scope: string;
}

export function ActionMenuItems({
  items,
  scope,
}: ActionMenuItemsProps): JSX.Element {
  return (
    <>
      {items.map((item) => {
        const reason = item.disabledReason;
        const disabled = reason !== undefined;
        const hintId = `${scope}-${item.id}-hint`;
        const reasonId = `${scope}-${item.id}-reason`;
        const describedBy =
          [item.hint !== undefined ? hintId : null, disabled ? reasonId : null]
            .filter((id): id is string => id !== null)
            .join(" ") || undefined;

        return (
          <React.Fragment key={item.id}>
            {item.separatorBefore ? <PopoverSeparator /> : null}
            {/* role="none" keeps descriptions outside the menu contract. */}
            <div role="none">
              <PopoverMenuItem
                // Spread, not `icon={item.icon}`: under
                // exactOptionalPropertyTypes an explicit `undefined` is not
                // assignable to an optional prop.
                {...(item.icon !== undefined ? { icon: item.icon } : {})}
                disabled={disabled}
                // Visible text alone leaves the reason unannounced when a
                // screen reader reads the item on its own.
                {...(describedBy !== undefined
                  ? { "aria-describedby": describedBy }
                  : {})}
                {...(!disabled && item.onSelect
                  ? { onClick: item.onSelect }
                  : {})}
                tone={item.tone}
              >
                {item.label}
              </PopoverMenuItem>
              {/*
                Both lines, not one: `hint` says what the action does and
                `reason` says why it cannot run. Coalescing them hides the
                reason on exactly the items that carry a hint, leaving a
                described action that inexplicably does nothing.
              */}
              {item.hint !== undefined && (
                <p
                  id={hintId}
                  className="ms-9 me-2.5 text-dense-1 text-pb-text-muted"
                >
                  {item.hint}
                </p>
              )}
              {disabled && (
                <p
                  id={reasonId}
                  className="mb-1 ms-9 me-2.5 text-dense-1 text-pb-text-muted"
                >
                  {reason}
                </p>
              )}
            </div>
          </React.Fragment>
        );
      })}
    </>
  );
}
